/**
 * Life Journey Engine — pure frontend helpers.
 *
 * Category/task ids come from the backend template catalog
 * (backend/app/core/journey_templates.py) and double as i18n key fragments:
 * hyphens map to underscores, e.g. task `housing-find-home` →
 * `journey.task.housing_find_home.name`.
 */

// Must match SNOOZE_ALLOWED_DAYS in backend/app/core/journey_engine.py.
export const SNOOZE_OPTIONS_DAYS = [1, 3, 7];

export const TASK_STATUS = {
  PENDING: 'pending',
  COMPLETED: 'completed',
  SNOOZED: 'snoozed',
  DISMISSED: 'dismissed',
};

const underscore = (id) => String(id || '').replace(/-/g, '_');

export const categoryLabelKey = (categoryId) => `journey.cat.${underscore(categoryId)}`;
export const taskNameKey = (taskId) => `journey.task.${underscore(taskId)}.name`;
export const taskDescKey = (taskId) => `journey.task.${underscore(taskId)}.desc`;
export const helpPromptKey = (categoryId) => `journey.help.prompt.${underscore(categoryId)}`;
export const snoozeLabelKey = (days) => `journey.snooze.days_${days}`;
export const recommendationKey = (rec) => rec?.message_key || 'journey.rec.overdue_task';
export const auditActionKey = (action) =>
  `journey.audit.${underscore(String(action || '').replace(/\./g, '-'))}`;

/** Status of a task from the journey's task_states map (default pending). */
export const taskStatus = (taskStates, taskId) =>
  (taskStates && taskStates[taskId] && taskStates[taskId].status) || TASK_STATUS.PENDING;

/** Categories of a template, partitioned into selected and unselected. */
export const partitionCategories = (template, selectedIds) => {
  const selected = new Set(selectedIds || []);
  const categories = (template && template.categories) || [];
  return {
    selected: categories.filter((cat) => selected.has(cat.id)),
    unselected: categories.filter((cat) => !selected.has(cat.id)),
  };
};

/** Per-category completed/total over non-dismissed tasks (mirrors backend math). */
export const categoryCounts = (category, taskStates) => {
  let total = 0;
  let completed = 0;
  (category.tasks || []).forEach((task) => {
    const status = taskStatus(taskStates, task.id);
    if (status === TASK_STATUS.DISMISSED) return;
    total += 1;
    if (status === TASK_STATUS.COMPLETED) completed += 1;
  });
  return { total, completed };
};
